"use client";

import { ChevronDown, ChevronUp } from "lucide-react";

import { Badge } from "@/lib/components/badge";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/lib/components/table";

interface LeaderboardItem {
	model: string;
	provider: string;
	requestCount: number;
	appCount: number;
	avgLatency: number;
	successRate: number;
	costPer1kTokens: number;
	trendingPercent: number | null;
}

interface LeaderboardTableProps {
	data: LeaderboardItem[];
	onSort?: (column: string) => void;
}

const PROVIDER_COLORS: Record<string, string> = {
	openai: "bg-green-100 text-green-800",
	anthropic: "bg-blue-100 text-blue-800",
	google: "bg-yellow-100 text-yellow-800",
	cohere: "bg-purple-100 text-purple-800",
	meta: "bg-pink-100 text-pink-800",
	default: "bg-gray-100 text-gray-800",
};

export function LeaderboardTable({ data }: LeaderboardTableProps) {
	const getProviderColor = (provider: string) => {
		return PROVIDER_COLORS[provider.toLowerCase()] || PROVIDER_COLORS.default;
	};

	return (
		<div className="overflow-x-auto rounded-lg border">
			<Table className="min-w-[1000px]">
				<TableHeader>
					<TableRow>
						<TableHead className="w-12">Rank</TableHead>
						<TableHead>Model</TableHead>
						<TableHead>Provider</TableHead>
						<TableHead className="text-right">Apps</TableHead>
						<TableHead className="text-right">Requests</TableHead>
						<TableHead className="text-right">Avg Latency</TableHead>
						<TableHead className="text-right">Success %</TableHead>
						<TableHead className="text-right">Cost/1K</TableHead>
						<TableHead className="text-right">Trending</TableHead>
					</TableRow>
				</TableHeader>
				<TableBody>
					{data.map((item, index) => (
						<TableRow key={`${item.model}-${item.provider}`}>
							<TableCell className="font-medium">#{index + 1}</TableCell>
							<TableCell className="font-medium">{item.model}</TableCell>
							<TableCell>
								<Badge className={getProviderColor(item.provider)}>
									{item.provider}
								</Badge>
							</TableCell>
							<TableCell className="text-right text-sm">
								{item.appCount}
							</TableCell>
							<TableCell className="text-right text-sm">
								{item.requestCount.toLocaleString()}
							</TableCell>
							<TableCell className="text-right text-sm">
								{item.avgLatency}ms
							</TableCell>
							<TableCell className="text-right text-sm">
								{item.successRate.toFixed(1)}%
							</TableCell>
							<TableCell className="text-right text-sm">
								${item.costPer1kTokens.toFixed(2)}
							</TableCell>
							<TableCell className="text-right text-sm">
								{item.trendingPercent !== null ? (
									<div
										className={`flex items-center justify-end gap-1 ${
											item.trendingPercent >= 0
												? "text-status-ok-title"
												: "text-status-error-title"
										}`}
									>
										{item.trendingPercent >= 0 ? (
											<ChevronUp className="h-4 w-4" />
										) : (
											<ChevronDown className="h-4 w-4" />
										)}
										{Math.abs(item.trendingPercent).toFixed(1)}%
									</div>
								) : (
									<span className="text-muted-foreground">-</span>
								)}
							</TableCell>
						</TableRow>
					))}
				</TableBody>
			</Table>
		</div>
	);
}
